import { Box, Typography } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Client from "./client";
import { PhotoModal } from "./photoModal";
import { tokens } from "../../theme";
import { IconAlert, IconCamera, IconChevronLeft, IconChevronRight, IconClose } from "../ui/icons";
import { useIsMobile } from "../../hooks/useIsMobile";
// import supabase from '../../supabase';

const Photos = ({
  panel = false,
  formState,
  setFormState,
  isOrderClosed,
  setDeletedPictures,
  orderStatus,
  isEdition,
  hideClientInfo,
  isReadOnlyMode,
  isOfflineMode,
}) => {
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [photoUrlBakground, setPhotoUrlBakground] = useState("");
  const myInput = useRef(null);
  const isMobile = useIsMobile();
  const photoHeight = isMobile ? 200 : 320;
  /** En móvil: cuadrado (ancho = alto), acotado por alto y por el viewport */
  const mobileSquareSide = `min(${photoHeight}px, calc(100vw - 48px))`;
  
  // Obtener el estado de administrador desde Redux
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const isAdminStorage = sessionStorage.getItem("isAdmin") === "true";
  
  // Permitir edición si la orden está pendiente (1), es admin, o se está creando una nueva orden (orderStatus undefined/null)
  // Pero no permitir edición si está en modo de solo lectura O en modo offline
  const canEdit = !isReadOnlyMode && !isOfflineMode && (orderStatus === 1 || orderStatus === undefined || orderStatus === null || isAdmin || isAdminStorage);

  const handleFileChange = (selectedFiles) => {
    const newPictures = Array.from(selectedFiles).map((file) => ({
      picture: file,
      isPictureAddedOnEdition: isEdition ? true : !isOrderClosed,
    }));

    setFormState({
      ...formState,
      pictures: [...formState.pictures, ...newPictures],
    });
  };

  const removePhoto = (index) => {
    const updatedFiles = [...formState.pictures];
    const imageToRemove = updatedFiles[index];
    updatedFiles.splice(index, 1);
    setFormState({
      ...formState,
      pictures: updatedFiles,
    });

    // Si es una imagen ya subida (string url), marcarla para eliminación temporal
    if (typeof imageToRemove === 'string') {
      try {
        // Extraer el nombre del archivo del url
        const parts = imageToRemove.split("/");
        const extractedPart = parts.slice(-1)[0];
        
        // Limpiar parámetros de URL si existen (como ?v=123)
        const cleanFileName = extractedPart.split('?')[0];
               
        // Agregar a deletedPictures para eliminación posterior cuando se confirme
        setDeletedPictures((prevState) => {
          const newState = [...prevState, cleanFileName];
          return newState;
        });
      } catch (e) {
        console.error('Error al procesar eliminación de imagen:', e);
        // Si ocurre un error inesperado, también agregar a deletedPictures
        setDeletedPictures((prevState) => {
          const parts = imageToRemove.split("/");
          const extractedPart = parts.slice(-1)[0];
          const cleanFileName = extractedPart.split('?')[0];
          return [...prevState, cleanFileName];
        });
      }
    }
  };

  const pictures = formState.pictures || [];
  const trackRef = useRef(null);

  const CarouselArrow = ({ side, onClick }) => (
    <Box
      role="button"
      aria-label={side === "left" ? "Fotos anteriores" : "Fotos siguientes"}
      onClick={onClick}
      sx={{
        display: { xs: "none", md: "flex" },
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [side]: -14,
        width: 32,
        height: 32,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        backgroundColor: tokens.surface,
        border: `1px solid ${tokens.border}`,
        boxShadow: tokens.shadowSm,
        color: tokens.ink2,
        zIndex: 2,
        "&:hover": { color: tokens.brandDark, borderColor: tokens.borderStrong },
      }}
    >
      {side === "left" ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
    </Box>
  );

  /** Desplaza el carril casi una pantalla, dejando una foto de referencia. */
  const scrollTrack = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * (track.clientWidth * 0.8), behavior: "smooth" });
  }, []);

  /** Cada foto es una tarjeta cuadrada: se ve igual esté subida o recién elegida. */
  const PhotoTile = ({ src, index }) => (
    <Box
      sx={{
        position: "relative",
        flex: "0 0 auto",
        scrollSnapAlign: "start",
        width: panel ? "auto" : { xs: 150, sm: 180, md: 200 },
        aspectRatio: "1 / 1",
        borderRadius: `${tokens.radiusCard}px`,
        overflow: "hidden",
        border: `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface2,
        cursor: "zoom-in",
        "&:hover .photo-overlay": { opacity: 1 },
      }}
      onClick={() => {
        setPhotoUrlBakground(src);
        setOpenPhotoModal(true);
      }}
    >
      <Box
        component="img"
        src={src}
        alt={`Foto ${index + 1} del trabajo`}
        loading="lazy"
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />

      <Box
        className="photo-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "flex-end",
          padding: "8px",
          background: "linear-gradient(to top, rgba(28,26,25,0.55), transparent 45%)",
          opacity: { xs: 1, md: 0 },
          transition: "opacity 140ms ease",
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#fff",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {index + 1}
        </Typography>
      </Box>

      {canEdit && (
        <Box
          role="button"
          aria-label={`Quitar foto ${index + 1}`}
          onClick={(e) => {
            e.stopPropagation();
            removePhoto(index);
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 28,
            height: 28,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.94)",
            color: tokens.brandDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: tokens.shadowSm,
            "&:hover": { backgroundColor: "#fff", color: tokens.brandDarker },
          }}
        >
          <IconClose size={15} />
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      className="step_container photo_upload_container"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: panel ? "12px" : "16px",
        width: "100%",
        minWidth: 0,
        ...(panel && {
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: `${tokens.radiusCard}px`,
          padding: "16px",
        }),
      }}
    >
      {!hideClientInfo && <Client formState={formState} />}

      <input
        ref={myInput}
        style={{ display: "none" }}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFileChange(e.target.files)}
      />

      {isOfflineMode && !isReadOnlyMode && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "12px 14px",
            borderRadius: `${tokens.radiusCard}px`,
            backgroundColor: "#fbf1e2",
            border: "1px solid #efdcb9",
            color: "#8a5a12",
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0, paddingTop: "1px" }}>
            <IconAlert size={18} />
          </Box>
          <Typography sx={{ fontSize: "13px", lineHeight: 1.45 }}>
            <strong>Sin buena conexión.</strong> Cargá las fotos más tarde y seguí con el
            paso siguiente; la orden se guarda igual.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography sx={{ fontSize: panel ? "14px" : "13px", fontWeight: 600, flexGrow: 1 }}>
            Fotos del trabajo
          </Typography>
          <Typography
            sx={{ fontSize: "11.5px", color: tokens.ink3, fontVariantNumeric: "tabular-nums" }}
          >
            {pictures.length === 0
              ? "Ninguna todavía"
              : `${pictures.length} ${pictures.length === 1 ? "foto" : "fotos"}`}
          </Typography>
        </Box>

        {/* Carril horizontal: una sola fila, siempre a todo el ancho disponible. */}
        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            ref={trackRef}
            className="images_container"
            sx={{
              display: panel ? "grid" : "flex",
              gridTemplateColumns: panel ? "repeat(3, minmax(0, 1fr))" : undefined,
              maxHeight: panel ? 300 : "none",
              overflowY: panel ? "auto" : "visible",
              gap: "10px",
              width: "100%",
              overflowX: panel ? "hidden" : "auto",
              scrollSnapType: panel ? "none" : "x mandatory",
              scrollPaddingLeft: "0px",
              paddingBottom: "4px",
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: tokens.borderStrong,
                borderRadius: 999,
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            }}
          >
            {pictures.map((image, index) => {
              const src = image?.isPictureAddedOnEdition
                ? URL.createObjectURL(image.picture)
                : image;
              return (
                <PhotoTile
                  key={`${index}-${typeof image === "string" ? image : "local"}`}
                  src={src}
                  index={index}
                />
              );
            })}

            {canEdit && (
              <Box
                role="button"
                aria-label="Agregar fotos"
                onClick={() => myInput.current?.click()}
                sx={{
                  flex: panel ? "1 1 auto" : "0 0 auto",
                  scrollSnapAlign: "start",
                  width: panel ? "auto" : { xs: 150, sm: 180, md: 200 },
                  aspectRatio: "1 / 1",
                  borderRadius: `${tokens.radiusCard}px`,
                  border: `1px dashed ${tokens.borderStrong}`,
                  backgroundColor: tokens.surface,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  color: tokens.ink2,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: tokens.brand,
                    backgroundColor: tokens.brandTint2,
                    color: tokens.brandDark,
                  },
                }}
              >
                <IconCamera size={22} />
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>Agregar</Typography>
              </Box>
            )}
          </Box>

          {/* Flechas sólo donde hay mouse: en teléfono se desliza con el dedo. */}
          {!panel && pictures.length > 2 && (
            <>
              <CarouselArrow side="left" onClick={() => scrollTrack(-1)} />
              <CarouselArrow side="right" onClick={() => scrollTrack(1)} />
            </>
          )}
        </Box>

        {pictures.length === 0 && !canEdit && (
          <Typography sx={{ fontSize: "13px", color: tokens.ink3 }}>
            Esta orden no tiene fotos cargadas.
          </Typography>
        )}
      </Box>

      <PhotoModal
        open={openPhotoModal}
        setOpen={setOpenPhotoModal}
        image={photoUrlBakground}
      />
    </Box>
  );
};

export default Photos;
